import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { RateType } from '../types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** SQLite CURRENT_TIMESTAMP ("2026-10-23 01:01:08") is UTC zonder zone-aanduiding. */
const SQLITE_UTC = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/;

export function formatDate(iso: string, fmt = 'dd-MM-yyyy'): string {
  try {
    return format(SQLITE_UTC.test(iso) ? new Date(iso.replace(' ', 'T') + 'Z') : parseISO(iso), fmt, { locale: nl });
  } catch {
    return iso;
  }
}

export function formatDateLong(iso: string): string {
  return format(parseISO(iso), 'EEEE d MMMM yyyy', { locale: nl });
}

export function formatTime(time: string): string {
  return time.slice(0, 5);
}

export function formatMoney(n: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(n);
}

export function formatHours(n: number): string {
  return n.toFixed(2).replace('.', ',') + 'u';
}

export function rateTypeLabel(rt: RateType): string {
  const map: Record<RateType, string> = {
    regular: 'Dag',
    avond: 'Avond',
    nacht: 'Nacht',
    weekend: 'Weekend',
    feest: 'Feestdag',
    avond_feest: 'Avond + Feest',
    nacht_feest: 'Nacht + Feest',
    weekend_feest: 'Weekend + Feest',
    oudjaar: 'Oudejaar',
    overwerk: 'Overwerk',
    meerwerk: 'Meerwerk',
    verlof: 'Verlof',
    ziekte: 'Ziekte',
  };
  return map[rt] ?? rt;
}

export function rateTypeBadgeClass(rt: RateType): string {
  if (rt === 'regular') return 'badge-dag';
  if (rt === 'avond' || rt === 'avond_feest') return 'badge-avond';
  if (rt === 'nacht' || rt === 'nacht_feest') return 'badge-nacht';
  if (rt === 'weekend' || rt === 'weekend_feest') return 'badge-weekend';
  if (rt === 'feest' || rt === 'oudjaar') return 'badge-feest';
  if (rt === 'overwerk' || rt === 'meerwerk')
    return 'badge bg-violet-500/20 text-violet-300 ring-1 ring-inset ring-violet-500/30';
  if (rt === 'verlof') return 'badge bg-amber-500/20 text-amber-300 ring-1 ring-inset ring-amber-500/30';
  if (rt === 'ziekte') return 'badge bg-rose-500/20 text-rose-300 ring-1 ring-inset ring-rose-500/30';
  return 'badge-neutral';
}

/** Given start/end HH:mm and crosses_midnight, return a dominant rateType for quick badge */
export function dominantRateType(
  shiftDate: string,
  startTime: string,
  endTime: string,
  crossesMidnight: 0 | 1,
  holidayDates: Set<string>
): RateType {
  const dow = (() => {
    const d = new Date(shiftDate + 'T12:00:00Z');
    const js = d.getUTCDay();
    return js === 0 ? 7 : js;
  })();
  const isWeekend = dow === 6 || dow === 7;
  const isHoliday = holidayDates.has(shiftDate);
  const [sh] = startTime.split(':').map(Number);
  if (isWeekend && isHoliday) return 'weekend_feest';
  if (isWeekend) return 'weekend';
  if (isHoliday) return 'feest';
  if (crossesMidnight || sh >= 23) return 'nacht';
  if (sh >= 18) return 'avond';
  if (sh < 7) return 'nacht';
  return 'regular';
}

export function minutesBetween(dateFrom: string, dateTo: string): number {
  return differenceInMinutes(parseISO(dateTo), parseISO(dateFrom));
}

export function countdownToShift(shiftDate: string, startTime: string): string {
  const now = new Date();
  const target = new Date(`${shiftDate}T${startTime}:00`);
  const diffMin = Math.floor((target.getTime() - now.getTime()) / 60000);
  if (diffMin < 0) {
    const pastMin = Math.abs(diffMin);
    const h = Math.floor(pastMin / 60);
    const m = pastMin % 60;
    return `bezig · ${h}u ${m}m terug`;
  }
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  if (h > 48) return `over ${Math.floor(h / 24)} dagen`;
  return `over ${h}u ${m}m`;
}
